// Chrome-style endless runner used on the project/library processing wait.
// Time is in seconds and distance in pixels. Callers pass `dt` and `rng`;
// nothing here reads the clock, storage, or a canvas.

export const RUN_WIDTH = 640;
export const RUN_HEIGHT = 180;
export const GROUND_Y = 150;

export const GRAVITY = 2400;
export const JUMP_VELOCITY = -760;
export const START_SPEED = 260;
export const MAX_SPEED = 640;
export const ACCEL = 20;

export const CLIPPER_X = 64;
export const CLIPPER_WIDTH = 42;
export const CLIPPER_HEIGHT = 52;
export const CLIPPER_DUCK_HEIGHT = 28;
export const HITBOX_INSET_X = 6;
export const HITBOX_INSET_Y = 4;

export const BIRD_GATE_DISTANCE = 1200;
export const FIRST_GAP = 380;
export const MIN_GAP = 240;
export const GAP_SHRINK = 60;
export const GAP_FLOOR = 180;
export const GAP_VARIANCE = 90;
export const SCORE_DIVISOR = 20;

export const MAX_FRAME_SECONDS = 0.05;
export const MAX_SLICE_PIXELS = 8;
export const MAX_SPAWNS_PER_STEP = 3;
export const SPAWN_MARGIN = 8;
export const OFFSCREEN_X = -20;

export type Phase = 'ready' | 'playing' | 'dead';

export type ObstacleKind = 'small' | 'tall' | 'wide' | 'bird-low' | 'bird-high';

export interface ObstacleSpec {
  width: number;
  height: number;
  /** Pixels from the ground up to the bottom of the obstacle. */
  lift: number;
}

export const OBSTACLES: Record<ObstacleKind, ObstacleSpec> = {
  small: { width: 18, height: 34, lift: 0 },
  tall: { width: 22, height: 52, lift: 0 },
  wide: { width: 38, height: 36, lift: 0 },
  'bird-low': { width: 32, height: 16, lift: 32 },
  'bird-high': { width: 32, height: 18, lift: 64 },
};

export interface Obstacle {
  id: number;
  kind: ObstacleKind;
  x: number;
  width: number;
  height: number;
  lift: number;
}

export interface ClipperRunState {
  phase: Phase;
  /** Screen y of the feet. `GROUND_Y` is the ground; jumping decreases it. */
  feetY: number;
  vy: number;
  ducking: boolean;
  speed: number;
  distance: number;
  untilNext: number;
  obstacles: Obstacle[];
  nextId: number;
}

export interface RunBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function createClipperRun(): ClipperRunState {
  return {
    phase: 'ready',
    feetY: GROUND_Y,
    vy: 0,
    ducking: false,
    speed: START_SPEED,
    distance: 0,
    untilNext: FIRST_GAP,
    obstacles: [],
    nextId: 1,
  };
}

function onGround(state: ClipperRunState): boolean {
  return state.feetY >= GROUND_Y - 0.01;
}

/** Starts a ready game, hops while grounded, and restarts after a crash. */
export function jumpClipperRun(state: ClipperRunState): ClipperRunState {
  if (state.phase === 'playing') {
    if (!onGround(state)) return state;
    return { ...state, vy: JUMP_VELOCITY, ducking: false };
  }
  return { ...createClipperRun(), phase: 'playing', vy: JUMP_VELOCITY };
}

/**
 * Ducking only sticks while the run is playing and the feet are on the
 * ground. Releasing always clears it. An airborne request clears it too,
 * so a held duck cannot shrink the hitbox in the air.
 */
export function setClipperDuck(state: ClipperRunState, ducking: boolean): ClipperRunState {
  if (!ducking) {
    if (!state.ducking) return state;
    return { ...state, ducking: false };
  }
  if (state.phase !== 'playing' || !onGround(state)) {
    if (!state.ducking) return state;
    return { ...state, ducking: false };
  }
  if (state.ducking) return state;
  return { ...state, ducking: true };
}

export function clipperRunScore(state: ClipperRunState): number {
  return Math.floor(state.distance / SCORE_DIVISOR);
}

export function clipperBox(state: ClipperRunState): RunBox {
  const spriteHeight = state.ducking ? CLIPPER_DUCK_HEIGHT : CLIPPER_HEIGHT;
  return {
    x: CLIPPER_X + HITBOX_INSET_X,
    y: state.feetY - spriteHeight + HITBOX_INSET_Y,
    width: CLIPPER_WIDTH - HITBOX_INSET_X * 2,
    height: spriteHeight - HITBOX_INSET_Y * 2,
  };
}

export function obstacleBox(obstacle: Obstacle): RunBox {
  return {
    x: obstacle.x,
    y: GROUND_Y - obstacle.lift - obstacle.height,
    width: obstacle.width,
    height: obstacle.height,
  };
}

function boxesOverlap(a: RunBox, b: RunBox): boolean {
  return a.x < b.x + b.width
    && a.x + a.width > b.x
    && a.y < b.y + b.height
    && a.y + a.height > b.y;
}

export function clipperHitsObstacle(state: ClipperRunState, obstacle: Obstacle): boolean {
  return boxesOverlap(clipperBox(state), obstacleBox(obstacle));
}

function clamp01(value: number): number {
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

/** Leading-edge spacing leaves time to land and jump again as speed increases. */
export function nextGap(speed: number, rng: () => number = () => 0): number {
  const t = clamp01((speed - START_SPEED) / (MAX_SPEED - START_SPEED));
  const minGap = MIN_GAP - GAP_SHRINK * t;
  const airTime = (2 * Math.abs(JUMP_VELOCITY)) / GRAVITY;
  const landingGap = speed * (airTime + 0.15);
  return Math.max(GAP_FLOOR, minGap, landingGap) + rng() * GAP_VARIANCE;
}

function rollKind(distance: number, rng: () => number): ObstacleKind {
  const roll = rng();
  if (distance >= BIRD_GATE_DISTANCE && roll > 0.78) {
    return rng() > 0.45 ? 'bird-high' : 'bird-low';
  }
  if (roll > 0.62) return 'tall';
  if (roll > 0.38) return 'wide';
  return 'small';
}

export function spawnObstacle(
  distance: number,
  x: number,
  id: number,
  rng: () => number,
): Obstacle {
  const kind = rollKind(distance, rng);
  const spec = OBSTACLES[kind];
  return { id, kind, x, width: spec.width, height: spec.height, lift: spec.lift };
}

/**
 * Advances a playing run. Ready and dead states return the same reference.
 * `dt` is clamped, then split so a cactus cannot pass through Clipper in one
 * long frame. `rng` decides obstacle kinds and gap jitter.
 */
export function stepClipperRun(
  state: ClipperRunState,
  dt: number,
  rng: () => number = Math.random,
): ClipperRunState {
  if (state.phase !== 'playing') return state;
  const stepDt = Math.min(Math.max(dt, 0), MAX_FRAME_SECONDS);
  if (stepDt === 0) return state;

  const travel = state.speed * stepDt;
  const slices = Math.max(1, Math.ceil(travel / MAX_SLICE_PIXELS));
  const sliceTravel = travel / slices;
  const sliceDt = stepDt / slices;

  let feetY = state.feetY;
  let vy = state.vy;
  let ducking = state.ducking;
  let distance = state.distance;
  let obstacles = state.obstacles;
  let phase: Phase = 'playing';

  for (let i = 0; i < slices; i += 1) {
    feetY += vy * sliceDt;
    vy += GRAVITY * sliceDt;
    if (feetY >= GROUND_Y) {
      feetY = GROUND_Y;
      vy = 0;
    } else {
      ducking = false;
    }
    distance += sliceTravel;
    obstacles = obstacles.map((obstacle) => ({ ...obstacle, x: obstacle.x - sliceTravel }));
    const probe: ClipperRunState = {
      ...state,
      feetY,
      vy,
      ducking,
      distance,
      obstacles,
    };
    if (obstacles.some((obstacle) => clipperHitsObstacle(probe, obstacle))) {
      phase = 'dead';
      vy = 0;
      break;
    }
    obstacles = obstacles.filter((obstacle) => obstacle.x + obstacle.width >= OFFSCREEN_X);
  }

  let untilNext = state.untilNext - travel;
  let nextId = state.nextId;
  if (phase === 'playing') {
    let spawned = 0;
    while (untilNext <= 0 && spawned < MAX_SPAWNS_PER_STEP) {
      const overshoot = -untilNext;
      const obstacle = spawnObstacle(
        distance,
        RUN_WIDTH + SPAWN_MARGIN - overshoot,
        nextId,
        rng,
      );
      nextId += 1;
      obstacles = [...obstacles, obstacle];
      untilNext += nextGap(state.speed, rng);
      spawned += 1;
      if (clipperHitsObstacle({ ...state, feetY, ducking, obstacles }, obstacle)) {
        phase = 'dead';
        vy = 0;
        break;
      }
    }
  }

  return {
    phase,
    feetY,
    vy,
    ducking,
    speed: Math.min(MAX_SPEED, state.speed + ACCEL * stepDt),
    distance,
    untilNext,
    obstacles,
    nextId,
  };
}
